import { FC } from 'react';
import { Tabs, Paper, ScrollArea, Group, ActionIcon, Box } from '@mantine/core';
import { Encounter, Patient } from '@medplum/fhirtypes';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { ExaminationContent } from '../examination/ExaminationContent';
import MedicalHistory from '../medical-history/MedicalHistory';
import { InterventionTab } from './InterventionTab';
import { ExaminationFilesTab } from './ExaminationFilesTab';
import { RecommendationsGeneticTestingTab } from './RecommendationsGeneticTestingTab';
import FamilyHistory from '../family-history/FamilyHistory';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { useScrollOverflow } from '@hooks/useScrollOverflow';

import classes from './CurrentEncounterTabs.module.css';

type CurrentEncounterTabsProps = {
  patient: Patient;
  encounter: Encounter;
};

export const CurrentEncounterTabs: FC<CurrentEncounterTabsProps> = ({
  patient,
  encounter,
}) => {
  const { t } = useTranslation(['patient-pages', 'lims']);
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    viewportRef,
    tabListRef,
    showScrollButtons,
    scrollLeft,
    scrollRight,
  } = useScrollOverflow();

  // Get active tab from URL parameter, default to 'questionnaire'
  const activeTab = searchParams.get('tab') || 'questionnaire';

  // Handle tab change by updating URL parameter
  const handleTabChange = (value: string | null) => {
    if (value) {
      setSearchParams({ tab: value });
    }
  };

  return (
    <Paper
      radius="md"
      withBorder
      mx="sm"
      mt="xl"
      className={classes.tabsContainer}
    >
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        classNames={{
          root: classes.tabsContent, // Use correct class context
        }}
      >
        <Box className={classes.stickyTabsHeader}>
          <Group gap="xs" wrap="nowrap">
            {showScrollButtons && (
              <ActionIcon variant="subtle" color="gray" onClick={scrollLeft}>
                <IconChevronLeft size={18} />
              </ActionIcon>
            )}
            <ScrollArea
              type="hover"
              scrollbarSize={0}
              offsetScrollbars
              viewportRef={viewportRef}
              flex={1}
            >
              <Tabs.List className={classes.tabsList} grow ref={tabListRef}>
                <Tabs.Tab value="questionnaire">
                  {t('currentEncounter.tabs.questionnaire', {
                    ns: 'patient-pages',
                  })}
                </Tabs.Tab>
                <Tabs.Tab value="family">
                  {t('currentEncounter.tabs.family', { ns: 'patient-pages' })}
                </Tabs.Tab>
                <Tabs.Tab value="examination">
                  {t('currentEncounter.tabs.examination', {
                    ns: 'patient-pages',
                  })}
                </Tabs.Tab>
                <Tabs.Tab value="drugs">
                  {t('currentEncounter.tabs.drugs', { ns: 'patient-pages' })}
                </Tabs.Tab>
                <Tabs.Tab value="tests">
                  {t('currentEncounter.tabs.tests', { ns: 'patient-pages' })}
                </Tabs.Tab>
                <Tabs.Tab value="recommendations">
                  {t('currentEncounter.tabs.recommendations', {
                    ns: 'patient-pages',
                  })}
                </Tabs.Tab>
              </Tabs.List>
            </ScrollArea>
            {showScrollButtons && (
              <ActionIcon variant="subtle" color="gray" onClick={scrollRight}>
                <IconChevronRight size={18} />
              </ActionIcon>
            )}
          </Group>
        </Box>

        <Box flex={1}>
          <Tabs.Panel value="questionnaire" pt="lg" px="md">
            <MedicalHistory />
          </Tabs.Panel>
          <Tabs.Panel value="family" pt="lg" px="md">
            <FamilyHistory />
          </Tabs.Panel>
          <Tabs.Panel value="examination" pt="lg" px="md">
            <ExaminationContent patient={patient} encounter={encounter} />
          </Tabs.Panel>
          <Tabs.Panel value="drugs" pt="lg" px="md">
            <InterventionTab patient={patient} encounter={encounter} />
          </Tabs.Panel>
          <Tabs.Panel value="tests" pt="lg" px="md">
            <ExaminationFilesTab patient={patient} encounter={encounter} />
          </Tabs.Panel>
          <Tabs.Panel value="recommendations" pt="lg" px="md">
            <RecommendationsGeneticTestingTab
              patient={patient}
              encounter={encounter}
            />
          </Tabs.Panel>
        </Box>
      </Tabs>
    </Paper>
  );
};
